import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, logout } from '../lib/authFetch';
import DispatchNav from '../components/DispatchNav';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, LogOut, RefreshCw, Search, ChevronRight, Download, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

const KIND_META = {
  vendor:  { title: 'Vendor Partnerships', empty: 'No partnership inquiries yet.' },
  job:     { title: 'Job Applications',    empty: 'No applications received yet.' },
  contact: { title: 'Contact Inquiries',   empty: 'No general contact messages yet.' },
  patient: { title: 'Healthcare Patient Transport Requests', empty: 'No requests yet.' },
  referral:{ title: 'Referral Partner Inquiries', empty: 'No referral inquiries yet.' },
};

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (['new'].includes(s)) return 'bg-amber-100 text-amber-800 border-amber-200';
  if (['in progress', 'under review', 'contacted', 'interview scheduled', 'documents requested'].includes(s)) return 'bg-sky-100 text-sky-800 border-sky-200';
  if (['qualified', 'approved', 'resolved', 'completed', 'assigned'].includes(s)) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (['declined', 'rejected', 'withdrawn', 'spam', 'cancelled', 'closed'].includes(s)) return 'bg-rose-100 text-rose-800 border-rose-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
}

function primaryLabel(kind, item) {
  if (kind === 'vendor' || kind === 'referral') return item.organization || item.contact_name || '—';
  if (kind === 'job') return item.full_name || '—';
  if (kind === 'contact') return item.name || item.email || '—';
  if (kind === 'patient') return item.requesting_organization || item.requestor_name || '—';
  return item.ref || '—';
}

function secondaryLabel(kind, item) {
  if (kind === 'vendor' || kind === 'referral') return `${item.org_type || '—'} · ${item.contact_name || ''}`.trim();
  if (kind === 'job') return `${item.position || '—'} · ${item.years_experience || '—'} yrs`;
  if (kind === 'contact') return item.subject_label || item.subject_key || 'General Inquiry';
  if (kind === 'patient') return item.requestor_name || '';
  return '';
}

const DispatchInquiries = () => {
  const { kind } = useParams();
  const navigate = useNavigate();
  const meta = KIND_META[kind] || { title: 'Inquiries', empty: 'Nothing here.' };

  const [items, setItems] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const runExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await authFetch(`/api/dispatch/exports/inquiries/${kind}.csv?${params.toString()}`);
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const disp = res.headers.get('content-disposition') || '';
      const m = /filename="?([^"]+)"?/i.exec(disp);
      const name = (m && m[1]) || `${kind}-export.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } finally { setExporting(false); }
  };

  const runBulkContact = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await authFetch(`/api/dispatch/inquiries/vendor/bulk-contact`, {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Bulk update failed'); return; }
      toast.success(`Marked ${data.modified} vendor inquir${data.modified === 1 ? 'y' : 'ies'} as Contacted`);
      clearSelection();
      setConfirmBulk(false);
      await fetchList(true);
    } catch { toast.error('Network error'); }
    finally { setBulkBusy(false); }
  };

  const fetchList = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await authFetch(`/api/dispatch/inquiries/${kind}?${params.toString()}`);
      if (!res.ok) {
        setError(`Unable to load list (HTTP ${res.status}).`);
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
      setStatusOptions(data.status_options || []);
    } catch (e) {
      setError('Network error while loading list.');
    } finally {
      setLoading(false);
    }
  }, [kind, q, statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="bg-[#004C97] shadow-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-white text-lg sm:text-xl font-semibold truncate" data-testid="inquiries-title">
              {meta.title}
            </h1>
            <p className="text-[11px] sm:text-xs text-white/70 truncate">
              {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={runExport}
              disabled={exporting}
              variant="ghost"
              className="text-white hover:bg-white/10 min-h-[44px] px-2"
              data-testid="inquiries-export-btn"
              title="Export CSV"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => fetchList(true)}
              variant="ghost"
              className="text-white hover:bg-white/10 min-h-[44px] px-2"
              data-testid="inquiries-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              onClick={async () => { await logout(); }}
              variant="ghost"
              className="text-white hover:bg-white/10 min-h-[44px] px-2"
              data-testid="inquiries-logout-btn"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <DispatchNav />

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        <Card>
          <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
                placeholder="Search by ref, organization, contact, email…"
                className="pl-9 min-h-[44px]"
                data-testid="inquiries-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="min-h-[44px] rounded-md border border-gray-300 px-3 text-sm"
              data-testid="inquiries-status-filter"
            >
              <option value="all">All statuses</option>
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button
              onClick={() => fetchList()}
              className="min-h-[44px] bg-[#004C97] hover:bg-[#003a78] text-white"
              data-testid="inquiries-apply-btn"
            >
              Apply
            </Button>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="inquiries-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12" data-testid="inquiries-loading">
            <Loader2 className="mx-auto w-8 h-8 animate-spin text-[#004C97]" />
            <p className="mt-2 text-gray-600 text-sm">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500" data-testid="inquiries-empty">
              {meta.empty}
            </CardContent>
          </Card>
        ) : (
          <>
            {kind === 'vendor' && selected.size > 0 && (
              <div className="rounded-lg border border-[#004C97] bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-2 sticky top-[110px] z-10 shadow-md" data-testid="inquiries-bulk-bar">
                <span className="text-sm font-medium text-gray-800">
                  {selected.size} selected
                </span>
                <div className="flex gap-2 ml-auto">
                  <Button onClick={clearSelection} variant="outline" className="min-h-[36px]" data-testid="inquiries-bulk-clear">
                    <XIcon className="w-4 h-4 mr-1" /> Clear
                  </Button>
                  <Button onClick={() => setConfirmBulk(true)} className="min-h-[36px] bg-[#004C97] hover:bg-[#003a78] text-white" data-testid="inquiries-bulk-contact-btn">
                    <Check className="w-4 h-4 mr-1" /> Mark as Contacted
                  </Button>
                </div>
              </div>
            )}
            <ul className="space-y-2" data-testid="inquiries-list">
              {filtered.map((item) => (
                <li key={item.id}>
                  <div className="w-full bg-white border border-gray-200 hover:border-[#004C97] rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition flex items-start gap-3">
                    {kind === 'vendor' && (
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                        className="mt-1 h-4 w-4 accent-[#004C97]"
                        data-testid={`inquiries-select-${item.id}`}
                        aria-label={`Select ${item.ref}`}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(`/dispatch/inquiries/${kind}/${item.id}`)}
                      className="flex-1 text-left"
                      data-testid={`inquiries-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[11px] text-gray-500">{item.ref || item.id.slice(0, 8)}</span>
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusBadgeClass(item.status)}`}>
                              {item.status || 'New'}
                            </span>
                            {kind === 'job' && typeof item.rating === 'number' && item.rating > 0 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid={`inquiries-item-rating-${item.id}`}>
                                ★ {item.rating}/5
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-[15px] font-semibold text-[#004C97] truncate">
                            {primaryLabel(kind, item)}
                          </p>
                          <p className="text-xs text-gray-600 truncate">{secondaryLabel(kind, item)}</p>
                          <p className="text-[11px] text-gray-500 mt-1">
                            {item.email || ''} · {item.phone || ''} ·{' '}
                            <time>{item.created_at ? new Date(item.created_at).toLocaleString() : ''}</time>
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Bulk confirm modal */}
        {confirmBulk && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="inquiries-bulk-confirm">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
              <h2 className="text-lg font-semibold text-[#004C97]">Mark {selected.size} vendor {selected.size === 1 ? 'inquiry' : 'inquiries'} as Contacted?</h2>
              <p className="mt-2 text-sm text-gray-600">
                This only updates vendor partnership records. Transportation Requests, Job Applications, and other lists are not affected.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmBulk(false)} disabled={bulkBusy} data-testid="inquiries-bulk-cancel">Cancel</Button>
                <Button onClick={runBulkContact} disabled={bulkBusy} className="bg-[#004C97] hover:bg-[#003a78] text-white" data-testid="inquiries-bulk-confirm-btn">
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  Yes, mark as Contacted
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DispatchInquiries;
